import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Payroll.css";
import {
  generateBankFilePDF,
  generatePayrollSummaryPDF,
} from "../../utils/payrollPdfGenerator";

const PAYROLL_PROCESS_CONTEXT_KEY = "cedur_payroll_process_context";

function ProcessPayroll() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const employeesMissingBankRows =
    payrollData?.emp_salary?.filter((employee) => {
      const accountNumber = employee?.bank_account_number || employee?.account_number;
      const ifscCode = employee?.bank_ifsc_code || employee?.ifsc_code || employee?.ifsc;
      const bankName = employee?.bank_name;
      const branchName = employee?.bank_branch_name || employee?.branch_name;

      return !accountNumber || !ifscCode || !bankName || !branchName;
    }) || [];

  const employeesMissingBankDetails = employeesMissingBankRows.length;

  const formatNumber = (num) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);

  const fetchProcessedPayroll = useCallback(async () => {
    try {
      const savedContext = (() => {
        try {
          return JSON.parse(sessionStorage.getItem(PAYROLL_PROCESS_CONTEXT_KEY) || "null");
        } catch (error) {
          return null;
        }
      })();
      let selectedMonth = location.state?.selectedMonth || savedContext?.selectedMonth;
      let engineVersionId =
        location.state?.engineVersionId ?? savedContext?.engineVersionId;

      if (!selectedMonth) {
        const generateResponse = await api.get("/payroll/generate");
        if (generateResponse.data?.success) {
          const generateData = generateResponse.data?.data || {};
          selectedMonth = generateData.selected_month_key || "";
          engineVersionId =
            engineVersionId ??
            generateData.selected_engine_version_id ??
            generateData.latest_version_id ??
            null;
        }
      }

      if (!selectedMonth) {
        throw new Error("Missing payroll month context. Please generate payroll again.");
      }

      const response = await api.post("/payroll/process", {
        selected_month: selectedMonth,
        engine_version_id: engineVersionId,
      });
      if (response.data.success) {
        setPayrollData(response.data.data);
        sessionStorage.setItem(
          PAYROLL_PROCESS_CONTEXT_KEY,
          JSON.stringify({
            selectedMonth,
            engineVersionId: response.data.data.engine_version_id ?? engineVersionId ?? null,
          }),
        );
      }
    } catch (error) {
      console.error("Failed to process payroll:", error);
      const message =
        error.response?.data?.message || error.message || "Failed to process payroll";
      Swal.fire({ icon: "error", title: "Error", text: message }).then(() =>
        navigate("/payroll/generate"),
      );
    } finally {
      setLoading(false);
    }
  }, [location.state?.engineVersionId, location.state?.selectedMonth, navigate]);

  const fetchCompanyDetails = useCallback(async () => {
    try {
      const response = await api.get("/profile");
      if (response.data.success) {
        const profile = response.data.data || {};
        setCompanyDetails({
          name: profile.company_name || "Cedur",
          address: profile.company_address || "",
          pan: profile.company_pan || "",
          gst: profile.company_gst || "",
          email: profile.email || "",
          phone: profile.phone_number || "",
          logo: profile.profile_photo_path || "",
          contactName: [profile.first_name, profile.last_name]
            .filter(Boolean)
            .join(" "),
          designation: profile.designation_name || "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch company details:", error);
    }
  }, []);

  useEffect(() => {
    fetchProcessedPayroll();
    fetchCompanyDetails();
  }, [fetchCompanyDetails, fetchProcessedPayroll]);

  const handleSubmitPayroll = async () => {
    const result = await Swal.fire({
      title: "Submit Payroll?",
      html: `
        <p>You are about to submit payroll for <strong>${payrollData.month} ${payrollData.year}</strong></p>
        <p>Total Employees: <strong>${payrollData.emp_salary.length}</strong></p>
        <p>Total Payroll Cost: <strong>Rs ${formatNumber(payrollData.total_payroll_cost)}</strong></p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6557ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit it!",
    });

    if (!result.isConfirmed) return;

    setSubmitting(true);

    try {
      // Include the processed snapshot metadata so this saved payroll run remains reproducible.
      const submitData = {
        month: payrollData.month,
        year: payrollData.year,
        emp_salary: payrollData.emp_salary,
        working_days: payrollData.working_days,
        weekly_holidays: payrollData.weekly_holidays,
        days_in_month: payrollData.days_in_month,
        financial_year: payrollData.financial_year,
        taxation_year_used: payrollData.taxation_year_used,
        selected_month_key: payrollData.selected_month_key,
        engine_version_id: payrollData.engine_version_id,
        engine_version_label: payrollData.engine_version_label,
      };

      const response = await api.post("/payroll/submit", submitData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Payroll Submitted!",
          html: `
            <p>${response.data.message}</p>
            <p style="margin-top:8px;font-size:13px;color:#666;">
              Attendance and leave records for <strong>${payrollData.month} ${payrollData.year}</strong> have been locked.
            </p>
          `,
          timer: 3000,
          showConfirmButton: false,
        });
        navigate("/payroll/list");
      }
    } catch (error) {
      // Handle the locked-month response separately so the user gets a clearer explanation.
      if (error.response?.data?.locked) {
        Swal.fire({
          icon: "warning",
          title: "Record Locked",
          text: error.response.data.message,
          confirmButtonColor: "#6557ff",
        });
      } else {
        const message = error.response?.data?.message || "Failed to submit payroll";
        Swal.fire({ icon: "error", title: "Error", text: message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    const result = await Swal.fire({
      title: "Download PDF",
      text: "Choose PDF type to download",
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Bank File",
      denyButtonText: "Payroll Report",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#6557ff",
      denyButtonColor: "#10b981",
      cancelButtonColor: "#d33",
    });

    if (!result.isConfirmed && !result.isDenied) {
      return;
    }

    try {
      let fileName = "";
      let successMessage = "";

      if (result.isConfirmed) {
        if (employeesMissingBankDetails > 0) {
          const warningResult = await Swal.fire({
            title: "Bank Details Missing",
            html: `
              <p><strong>${employeesMissingBankDetails}</strong> employee${employeesMissingBankDetails > 1 ? "s have" : " has"} incomplete bank details.</p>
              <p style="margin-top:8px;font-size:13px;color:#666;">
                The PDF will highlight the missing cells so you can review them before sharing with the bank.
              </p>
            `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#6557ff",
            cancelButtonColor: "#d33",
            confirmButtonText: "Download Anyway",
            cancelButtonText: "Cancel",
          });

          if (!warningResult.isConfirmed) {
            return;
          }
        }

        fileName = await Promise.resolve(
          generateBankFilePDF(payrollData, companyDetails),
        );
        successMessage = "Bank file PDF has been downloaded";
      } else if (result.isDenied) {
        fileName = await generatePayrollSummaryPDF(payrollData, companyDetails);
        successMessage = "Summary payroll PDF has been downloaded";
      }

      Swal.fire({
        icon: "success",
        title: "Downloaded!",
        text: fileName ? `${successMessage}: ${fileName}` : successMessage,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to generate payroll PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: error.message || "Could not generate the PDF file.",
      });
    }
  };


  const toggleRow = (employeeId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  // Tfoot sum helper
  const sumField = (field) =>
    payrollData.emp_salary.reduce((sum, emp) => sum + (emp[field] || 0), 0);

  if (loading) {
    return <MainLayout><PageLoader force copy="Processing payroll calculations..." /></MainLayout>;
  }

  if (!payrollData) {
    return (
      <MainLayout>
        <div className="error-container">
          <p>No payroll data available</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="process-payroll-page">

        {/* Header */}
        <div className="page-header-section">
          <div>
            <h4>{payrollData.month} {payrollData.year}</h4>
            <p className="subtitle">
              Working Days: {payrollData.working_days} | Weekly Holidays: {payrollData.weekly_holidays}
              {payrollData.financial_year && (
                <span> | FY: {payrollData.financial_year}</span>
              )}
            </p>
            {employeesMissingBankDetails > 0 && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "12px 14px",
                  borderRadius: "12px",
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                  maxWidth: "720px",
                }}
              >
                <p
                  className="subtitle"
                  style={{ color: "#b45309", margin: 0, fontWeight: 600 }}
                >
                  Bank details are incomplete for {employeesMissingBankDetails} employee{employeesMissingBankDetails > 1 ? "s" : ""}. The bank file will highlight missing fields.
                </p>
                <div
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {employeesMissingBankRows.map((employee) => (
                    <button
                      key={employee.id || employee.code}
                      type="button"
                      onClick={() =>
                        navigate(`/employees/edit/${employee.id}`, {
                          state: {
                            returnTo: "/payroll/process",
                            returnState: {
                              selectedMonth:
                                payrollData?.selected_month_key ||
                                location.state?.selectedMonth,
                              engineVersionId:
                                payrollData?.engine_version_id ??
                                location.state?.engineVersionId ??
                                null,
                              refreshBankWarning: true,
                            },
                          },
                        })
                      }
                      style={{
                        border: "1px solid #f59e0b",
                        background: "#ffffff",
                        color: "#92400e",
                        borderRadius: "999px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {(employee.full_name || `${employee.first_name || ""} ${employee.last_name || ""}`.trim() || employee.code)}
                      {employee.code ? ` (${employee.code})` : ""}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="header-actions">
            <button className="btn-download" onClick={handleDownloadPDF}>
              <i className="fas fa-download"></i> Download PDF
            </button>
            <button
              className="btn-submit"
              onClick={handleSubmitPayroll}
              disabled={submitting}
            >
              <i className="fas fa-check"></i>{" "}
              {submitting ? "Submitting..." : "Submit Payroll"}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon primary"><i className="fas fa-users"></i></div>
            <div className="card-content">
              <h3>{payrollData.emp_salary.length}</h3>
              <p>Total Employees</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon success"><i className="fas fa-money-bill-wave"></i></div>
            <div className="card-content">
              <h3>Rs {formatNumber(payrollData.total_gross_pay)}</h3>
              <p>Total Gross Pay</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon warning"><i className="fas fa-chart-line"></i></div>
            <div className="card-content">
              <h3>Rs {formatNumber(payrollData.total_net_pay)}</h3>
              <p>Total Net Pay</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon danger"><i className="fas fa-wallet"></i></div>
            <div className="card-content">
              <h3>Rs {formatNumber(payrollData.total_payroll_cost)}</h3>
              <p>Total Payroll Cost</p>
            </div>
          </div>
        </div>

        {/* Payroll Table */}
        <div className="table-card">
          <div className="table-header">
            <h5>Employee Salary Details</h5>
            <p className="payroll-table-note">
              Review the primary salary values below. Open Details for the employee&apos;s prior income and tax calculation breakdown.
            </p>
          </div>
          <div className="table-responsive">
            <table className="payroll-table payroll-detailed-table">
              <thead>
                <tr>
                  <th>Emp. Code</th>
                  <th>Name</th>
                  <th>Tax Regime</th>
                  <th>Attendance</th>
                  <th>Basic Salary</th>
                  <th>Gross Salary</th>
                  <th>Overtime Hours</th>
                  <th>Overtime Pay</th>
                  <th>Tax Deducted at Source</th>
                  <th>Professional Tax</th>
                  <th>Salary Advance Repayment</th>
                  <th>Reimbursement</th>
                  <th>Net Salary</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {payrollData.emp_salary.map((emp) => (
                  <React.Fragment key={emp.id}>
                    <tr className={emp.paid_days === 0 ? "zero-pay-row" : ""}>
                      <td>{emp.code}</td>
                      <td>{emp.full_name}</td>
                      <td>
                        <span className={`badge ${emp.tax_regime_used === "Old" ? "badge-warning" : "badge-info"}`}>
                          {emp.tax_regime_used}
                        </span>
                      </td>
                      <td>
                        <div className="attendance-cell">
                          <span>{emp.paid_days} / {payrollData.working_days} Days</span>
                          {emp.paid_days === 0 && (
                            <small className="attendance-note">No payable days in this month</small>
                          )}
                        </div>
                      </td>
                      <td>Rs {formatNumber(emp.basicmonthly)}</td>
                      <td>Rs {formatNumber(emp.gross_pay)}</td>
                      <td>{formatNumber(emp.totalOtHours)}</td>
                      <td>Rs {formatNumber(emp.otPay)}</td>
                      <td>Rs {formatNumber(emp.tenetive_tds)}</td>
                      <td>Rs {formatNumber(emp.monthly_prof_tax)}</td>
                      <td>Rs {formatNumber(emp.loanAmount)}</td>
                      <td className="reimbursement-amount">Rs {formatNumber(emp.reimbursement_amount)}</td>
                      <td className="net-salary">Rs {formatNumber(emp.net_salary)}</td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="action-btn view-btn"
                          onClick={() => toggleRow(emp.id)}
                          title={expandedRows[emp.id] ? "Hide tax details" : "Show tax details"}
                          aria-expanded={Boolean(expandedRows[emp.id])}
                          aria-label={`${expandedRows[emp.id] ? "Hide" : "Show"} tax details for ${emp.full_name}`}
                        >
                          <i className={`fas ${expandedRows[emp.id] ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                        </button>
                      </td>
                    </tr>
                    {expandedRows[emp.id] && (
                      <tr className="detail-row">
                        <td colSpan="14">
                          <div className="detail-grid payroll-tax-detail-grid">
                            <div className="detail-pill">
                              <span className="detail-label">Prior Received Gross</span>
                              <strong>Rs {formatNumber(emp.prior_income)}</strong>
                            </div>
                            <div className="detail-pill">
                              <span className="detail-label">Prior Deducted TDS</span>
                              <strong>Rs {formatNumber(emp.prior_tds)}</strong>
                            </div>
                            <div className="detail-pill">
                              <span className="detail-label">Projected Gross / Year</span>
                              <strong>Rs {formatNumber(emp.final_projected_gross ?? emp.grossYearlysalary)}</strong>
                            </div>
                            <div className="detail-pill">
                              <span className="detail-label">Gross Taxable Income</span>
                              <strong>Rs {formatNumber(emp.taxable_income_used)}</strong>
                            </div>
                            <div className="detail-pill">
                              <span className="detail-label">Calculated Tax</span>
                              <strong>Rs {formatNumber(emp.taxObligation)}</strong>
                            </div>
                            <div className="detail-pill">
                              <span className="detail-label">Surcharge</span>
                              <strong>Rs {formatNumber(emp.surcharge_amount)}</strong>
                            </div>
                            <div className="detail-pill">
                              <span className="detail-label">Deduction Under 87A</span>
                              <strong>Rs {formatNumber(emp.under_87a_tax)}</strong>
                            </div>
                            <div className="detail-pill">
                              <span className="detail-label">Education Cess</span>
                              <strong>Rs {formatNumber(emp.education_cess_tax)}</strong>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="4"><strong>Total</strong></td>
                  <td><strong>Rs {formatNumber(sumField("basicmonthly"))}</strong></td>
                  <td><strong>Rs {formatNumber(payrollData.total_gross_pay)}</strong></td>
                  <td><strong>{formatNumber(sumField("totalOtHours"))}</strong></td>
                  <td><strong>Rs {formatNumber(sumField("otPay"))}</strong></td>
                  <td><strong>Rs {formatNumber(sumField("tenetive_tds"))}</strong></td>
                  <td><strong>Rs {formatNumber(sumField("monthly_prof_tax"))}</strong></td>
                  <td><strong>Rs {formatNumber(sumField("loanAmount"))}</strong></td>
                  <td><strong>Rs {formatNumber(sumField("reimbursement_amount"))}</strong></td>
                  <td className="net-salary">
                    <strong>Rs {formatNumber(sumField("net_salary"))}</strong>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

      </div>
    </MainLayout>
  );
}

export default ProcessPayroll;
